/**
 * =============================================================================
 * Componente: ReferralAffiliateDetailSheet
 * Versão: 2.0.0 (Padronização visual com sheets de Indicação/Comissão/Saque)
 *
 * Descrição:
 *   Sheet lateral premium com detalhes completos de um AFILIADO. Padronizado
 *   1:1 com `ReferralReferralDetailSheet`, `CommissionDetailSheet` e
 *   `PayoutDetailSheet`:
 *
 *   - Sheet à direita, largura `w-[480px]` em desktop e fullscreen no mobile.
 *   - Header com gradient sutil + chip/avatar + nome + UUID curto + status
 *     badge (mesmo padrão visual dos demais sheets).
 *   - Corpo em `<ScrollArea>` com blocos seccionados (`SectionLabel` +
 *     cards arredondados com `InfoRow` alternando `bg-muted/20`).
 *   - Action bar contextual (aprovar/suspender/reprovar) preservada,
 *     respeitando lógica N:N (esconde botões quando há multi-programa).
 *   - Abas Resumo/Programas mantidas para o modelo N:N.
 *
 *   RELEASE:
 *     2026-04-26 v1.0.0 — Implementação inicial (legado).
 *     2026-05-02 v2.0.0 — Padronização visual com demais sheets do módulo
 *                          Indicação (header gradient, SectionLabel/InfoRow,
 *                          ScrollArea, blocos em cards arredondados).
 * =============================================================================
 */
import React, { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import {
  CheckCircle2,
  XCircle,
  PauseCircle,
  Loader2,
  Mail,
  Phone,
  Award,
  Calendar,
  IdCard,
  Link2,
  Layers,
  Check,
  X,
  Pause,
  RotateCcw,
  StickyNote,
  User as UserIcon,
  Clock,
  KeyRound,
  Wallet,
  TrendingUp,
  MousePointerClick,
  Users,
  Coins,
  Plus,
} from 'lucide-react';
import {
  useReferralAffiliate,
  useReferralAffiliateLinks,
  useReferralAffiliateMutations,
  type AffiliateRow,
  type AffiliateStatus,
  type ReferralAffiliateLinkRow,
} from '@/hooks/useReferralAffiliates';
import { LinkCard } from '@/components/affiliate/LinkCard';
// v1.4.0: modal de cadastro reaproveitado em modo "vincular a programa"
import { AffiliateAdminCreateModal } from '@/components/referral/AffiliateAdminCreateModal';
import { useAffiliateMembershipActions } from '@/hooks/useAffiliateMembershipActions';
import type { AffiliateMembership } from '@/hooks/useReferralAffiliatesPaginated';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AffiliateStatusBadge } from '@/components/referral/AffiliateStatusBadge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';
import { getPresenceState } from '@/lib/affiliate-presence';

// ============================================================================
// PROPS
// ============================================================================
interface Props {
  affiliateId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lista de adesões da pessoa (modelo N:N). Quando fornecida, ativa abas. */
  memberships?: AffiliateMembership[];
}

// ============================================================================
// HELPERS — espelham os demais sheets do módulo Indicação
// ============================================================================

function formatBRL(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

function formatLong(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return value;
  }
}

function shortId(id: string | null | undefined): string {
  if (!id) return '';
  return id.substring(0, 8).toUpperCase();
}

// ============================================================================
// SUBCOMPONENTES VISUAIS PADRONIZADOS
// (mesmas assinaturas usadas em ReferralReferralDetailSheet)
// ============================================================================

function SectionLabel({
  icon: Icon,
  children,
  right,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {children}
      </div>
      {right}
    </div>
  );
}

function InfoRow({
  label,
  value,
  icon: Icon,
  className,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex justify-between gap-3 px-3 py-2 text-sm rounded-md',
        className,
      )}
    >
      <span className="text-xs text-muted-foreground shrink-0 self-center inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground/70" />}
        {label}
      </span>
      <span className="text-right text-sm font-medium tabular-nums break-words self-center">
        {value ?? '—'}
      </span>
    </div>
  );
}

/** Card de métrica padronizado (alinhado ao visual dos demais sheets) */
function MetricCard({
  icon: Icon,
  label,
  value,
  highlight,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border p-2.5',
        highlight
          ? 'bg-primary/5 border-primary/20'
          : 'bg-card border-border/60',
      )}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="text-sm font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export function ReferralAffiliateDetailSheet({
  affiliateId,
  open,
  onOpenChange,
  memberships,
}: Props) {
  const { toast } = useToast();
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  const { data: affiliate, isLoading } = useReferralAffiliate(affiliateId);
  const { updateNotesMutation } = useReferralAffiliateMutations();
  const {
    approveMembership,
    suspendMembership,
    rejectMembership,
    reactivateMembership,
    isLoading: membershipActionBusy,
  } = useAffiliateMembershipActions();

  // Adesão única (quando há exatamente uma) — usada pelo painel principal
  const singleMembership: AffiliateMembership | null =
    Array.isArray(memberships) && memberships.length === 1
      ? memberships[0]
      : null;

  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (affiliate) {
      setNotes(affiliate.notes ?? '');
      setReason(affiliate.rejection_reason ?? '');
    }
  }, [affiliate?.id, affiliate?.notes, affiliate?.rejection_reason]);

  /** Roteamento de mudança de status para a RPC correta (modelo N:N). */
  const runStatusChange = async (
    status: AffiliateStatus,
    reasonValue: string | null,
    successMessage: string,
  ) => {
    if (!affiliate) return;
    if (!singleMembership) {
      toast({
        title: 'Use a aba "Programas"',
        description:
          'Este afiliado participa de mais de um programa. Modere cada adesão na aba "Programas".',
        variant: 'destructive',
      });
      return;
    }
    try {
      if (status === 'active') {
        if (singleMembership.status === 'suspended') {
          await reactivateMembership(singleMembership.id);
        } else {
          await approveMembership(singleMembership.id);
        }
      } else if (status === 'suspended') {
        await suspendMembership(singleMembership.id, reasonValue || undefined);
      } else if (status === 'rejected') {
        await rejectMembership(singleMembership.id, reasonValue || undefined);
      } else {
        throw new Error(`Status não suportado: ${status}`);
      }
      toast({ title: successMessage });
    } catch (err: any) {
      toast({
        title: 'Erro ao atualizar status',
        description: err?.message ?? 'Tente novamente.',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const handleApprove = async () => {
    if (!affiliate) return;
    const isReactivation = affiliate.status === 'suspended';
    const confirmed = await confirmDialog({
      title: isReactivation ? 'Reativar afiliado?' : 'Aprovar afiliado?',
      description: isReactivation
        ? 'O afiliado voltará a ter acesso à área de afiliado e seus links voltam a contar conversões.'
        : 'O afiliado terá acesso liberado à área de afiliado e seus links começam a contar conversões.',
      variant: 'info',
      confirmText: isReactivation ? 'Reativar' : 'Aprovar',
    });
    if (!confirmed) return;
    await runStatusChange(
      'active',
      null,
      isReactivation ? 'Afiliado reativado.' : 'Afiliado aprovado.',
    );
  };

  const openSuspendDialog = () => {
    setSuspendReason(reason || '');
    setSuspendOpen(true);
  };

  const confirmSuspend = async () => {
    const value = suspendReason.trim();
    if (!value) return;
    try {
      await runStatusChange('suspended', value, 'Afiliado suspenso.');
      setSuspendOpen(false);
      setSuspendReason('');
    } catch {
      // erro já tratado
    }
  };

  const openRejectDialog = () => {
    setRejectReason(reason || '');
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    const value = rejectReason.trim();
    if (!value) return;
    try {
      await runStatusChange('rejected', value, 'Afiliado reprovado.');
      setRejectOpen(false);
      setRejectReason('');
    } catch {
      // erro já tratado
    }
  };

  const handleSaveNotes = async () => {
    if (!affiliate) return;
    try {
      await updateNotesMutation.mutateAsync({
        id: affiliate.id,
        notes: notes.trim() || null,
      });
      toast({ title: 'Notas salvas.' });
    } catch (err: any) {
      toast({
        title: 'Erro ao salvar notas',
        description: err?.message ?? 'Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const statusBusy = membershipActionBusy;
  const hasMultiplePrograms =
    Array.isArray(memberships) && memberships.length > 1;
  // Status efetivo: prioriza adesão única (modelo N:N)
  const effectiveStatus = (singleMembership?.status ??
    affiliate?.status) as AffiliateStatus | undefined;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="!w-full sm:!w-[480px] sm:!max-w-[480px] p-0 overflow-x-hidden bg-background"
          aria-describedby={undefined}
        >
          {isLoading || !affiliate ? (
            <LoadingState />
          ) : (
            <ScrollArea className="h-full">
              {/* ============== Header com gradient sutil ============== */}
              <div className="relative bg-gradient-to-b from-muted/50 via-muted/15 to-transparent px-5 sm:px-6 pt-6 pb-5">
                <SheetHeader className="space-y-3 text-left">
                  <div className="flex items-start gap-3">
                    <SmartAvatar
                      src={affiliate.avatar_url}
                      name={affiliate.full_name || affiliate.email || '?'}
                      size="md"
                      use3DFallback
                    />
                    <div className="flex-1 min-w-0">
                      <SheetTitle className="text-base font-semibold leading-tight truncate">
                        {affiliate.full_name || 'Afiliado'}
                      </SheetTitle>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[11px] text-muted-foreground font-mono uppercase">
                          #{shortId(affiliate.id)}
                        </span>
                        {effectiveStatus && (
                          <>
                            <span className="text-muted-foreground/50">·</span>
                            <AffiliateStatusBadge status={effectiveStatus} />
                          </>
                        )}
                        {hasMultiplePrograms ? (
                          <>
                            <span className="text-muted-foreground/50">·</span>
                            <Badge
                              variant="outline"
                              className="text-[10px] h-5 px-2"
                            >
                              {memberships!.length} programas
                            </Badge>
                          </>
                        ) : singleMembership ? (
                          <>
                            <span className="text-muted-foreground/50">·</span>
                            <Badge
                              variant="outline"
                              className="text-[10px] h-5 px-2 gap-1.5"
                              title={`Programa: ${singleMembership.program_name ?? '—'}`}
                            >
                              <Layers className="h-3 w-3" />
                              <span className="truncate max-w-[160px]">
                                {singleMembership.program_name ?? 'Programa'}
                              </span>
                            </Badge>
                          </>
                        ) : null}
                      </div>
                      {affiliate.email && (
                        <p className="text-xs text-muted-foreground truncate mt-1">
                          {affiliate.email}
                        </p>
                      )}
                    </div>
                  </div>
                </SheetHeader>
              </div>

              {/* ============== Corpo ============== */}
              <div className="px-5 sm:px-6 pb-8 space-y-7 min-w-0">
                {/* v1.4.0: a aba "Programas" passa a existir SEMPRE, pois é
                    também o ponto de entrada para vincular o afiliado a um
                    novo programa (antes só aparecia com 2+ adesões). */}
                <Tabs defaultValue="summary">
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger value="summary">Resumo</TabsTrigger>
                    <TabsTrigger value="programs">
                      Programas
                      <Badge
                        variant="outline"
                        className="ml-2 text-[10px] h-4 px-1.5"
                      >
                        {memberships?.length ?? 0}
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="summary" className="mt-5 space-y-7">
                    <SummarySections
                      affiliate={affiliate}
                      notes={notes}
                      setNotes={setNotes}
                      reason={reason}
                      setReason={setReason}
                      onApprove={handleApprove}
                      onSuspend={openSuspendDialog}
                      onReject={openRejectDialog}
                      handleSaveNotes={handleSaveNotes}
                      statusBusy={statusBusy}
                      notesBusy={updateNotesMutation.isPending}
                      hasMultiplePrograms={hasMultiplePrograms}
                      memberships={memberships}
                      singleMembership={singleMembership}
                      effectiveStatus={effectiveStatus}
                    />
                  </TabsContent>
                  <TabsContent value="programs" className="mt-5">
                    <ProgramsTab
                      affiliateName={affiliate.full_name}
                      affiliate={affiliate}
                      memberships={memberships ?? []}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>

      {/* Diálogo de Suspensão — motivo obrigatório embutido ----------------- */}
      <Dialog
        open={suspendOpen}
        onOpenChange={(o) => !statusBusy && setSuspendOpen(o)}
      >
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30">
                <PauseCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              Suspender afiliado?
            </DialogTitle>
            <DialogDescription className="text-sm">
              O afiliado perderá acesso à área de afiliado e seus links param de
              contar conversões enquanto estiver suspenso.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="suspend-reason" className="text-sm font-medium">
              Motivo da suspensão <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="suspend-reason"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Ex: Suspeita de fraude / inatividade prolongada / solicitação interna..."
              rows={3}
              className="resize-none"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              O motivo é obrigatório e fica registrado para auditoria.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setSuspendOpen(false)}
              disabled={statusBusy}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmSuspend}
              disabled={!suspendReason.trim() || statusBusy}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {statusBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar suspensão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de Reprovação — motivo obrigatório embutido --------------- */}
      <Dialog
        open={rejectOpen}
        onOpenChange={(o) => !statusBusy && setRejectOpen(o)}
      >
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-destructive/10">
                <XCircle className="h-4 w-4 text-destructive" />
              </div>
              Reprovar cadastro?
            </DialogTitle>
            <DialogDescription className="text-sm">
              O cadastro do afiliado será reprovado e ele não conseguirá acessar
              a área de afiliado. Esta ação pode ser revertida posteriormente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="reject-reason" className="text-sm font-medium">
              Motivo da reprovação <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex: Documentação inválida / não atende aos critérios do programa..."
              rows={3}
              className="resize-none"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              O motivo é obrigatório e fica registrado para auditoria.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setRejectOpen(false)}
              disabled={statusBusy}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmReject}
              disabled={!rejectReason.trim() || statusBusy}
            >
              {statusBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar reprovação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {ConfirmDialogComponent}
    </>
  );
}

// ============================================================================
// SUBCOMPONENTE: Estado de carregamento (skeletons padronizados)
// ============================================================================
function LoadingState() {
  return (
    <div className="px-5 sm:px-6 pt-6 pb-8 space-y-6">
      <div className="flex items-start gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Seções do "Resumo" (compartilhado entre N:1 e N:N)
// ============================================================================
function SummarySections({
  affiliate,
  notes,
  setNotes,
  reason,
  setReason,
  onApprove,
  onSuspend,
  onReject,
  handleSaveNotes,
  statusBusy,
  notesBusy,
  hasMultiplePrograms,
  memberships,
  singleMembership,
  effectiveStatus,
}: {
  affiliate: AffiliateRow;
  notes: string;
  setNotes: (v: string) => void;
  reason: string;
  setReason: (v: string) => void;
  onApprove: () => void;
  onSuspend: () => void;
  onReject: () => void;
  handleSaveNotes: () => void;
  statusBusy: boolean;
  notesBusy: boolean;
  hasMultiplePrograms: boolean;
  memberships?: AffiliateMembership[];
  singleMembership?: AffiliateMembership | null;
  effectiveStatus?: AffiliateStatus;
}) {
  const status = effectiveStatus ?? affiliate.status;

  return (
    <>
      {/* ============== Bloco: Programa vinculado (v2.2.0 — exibido apenas no modo single) ============== */}
      {singleMembership && !hasMultiplePrograms && (
        <section className="space-y-3">
          <SectionLabel icon={Layers}>Programa</SectionLabel>
          <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
            <InfoRow
              icon={Layers}
              label="Nome"
              value={
                <span className="font-medium">
                  {singleMembership.program_name ?? '—'}
                </span>
              }
            />
            <InfoRow
              icon={Award}
              label="Slug"
              value={
                singleMembership.program_slug ? (
                  <span className="font-mono text-xs">
                    /{singleMembership.program_slug}
                  </span>
                ) : (
                  '—'
                )
              }
              className="bg-muted/20"
            />
            <InfoRow
              icon={CheckCircle2}
              label="Status da adesão"
              value={<AffiliateStatusBadge status={singleMembership.status} />}
            />
            <InfoRow
              icon={Calendar}
              label="Aderiu em"
              value={formatLong(singleMembership.created_at)}
              className="bg-muted/20"
            />
            {singleMembership.approved_at && (
              <InfoRow
                icon={CheckCircle2}
                label="Aprovado em"
                value={formatLong(singleMembership.approved_at)}
              />
            )}
          </div>
        </section>
      )}

      {/* ============== Bloco: Identificação ============== */}
      <section className="space-y-3">
        <SectionLabel icon={UserIcon}>Identificação</SectionLabel>
        <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
          <InfoRow icon={Phone} label="Telefone" value={affiliate.phone || '—'} />
          <InfoRow
            icon={IdCard}
            label="Documento"
            value={affiliate.document_number || '—'}
            className="bg-muted/20"
          />
          <InfoRow
            icon={Award}
            label="Slug"
            value={
              affiliate.personal_slug ? (
                <span className="font-mono">/{affiliate.personal_slug}</span>
              ) : (
                '—'
              )
            }
          />
          <InfoRow
            icon={Mail}
            label="E-mail"
            value={affiliate.email || '—'}
            className="bg-muted/20"
          />
          <InfoRow
            icon={KeyRound}
            label="ID"
            value={
              <span className="font-mono text-xs uppercase">
                #{shortId(affiliate.id)}
              </span>
            }
          />
          <InfoRow
            icon={Calendar}
            label="Criado em"
            value={formatLong(affiliate.created_at)}
            className="bg-muted/20"
          />
          <InfoRow
            icon={CheckCircle2}
            label="Aprovado em"
            value={formatLong(affiliate.approved_at)}
          />
          <InfoRow
            icon={Clock}
            label="Último login"
            value={formatLong(affiliate.last_login_at)}
            className="bg-muted/20"
          />
          {/* v2.1.0 — última atividade (presença / heartbeat) */}
          <InfoRow
            icon={Clock}
            label="Última atividade"
            value={(() => {
              const presence = getPresenceState((affiliate as any).last_seen_at);
              const when = formatLong((affiliate as any).last_seen_at);
              if (presence.status === 'online') {
                return (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-emerald-600 font-medium">Online agora</span>
                  </span>
                );
              }
              return `${when}${presence.status !== 'offline' ? ` (${presence.label})` : ''}`;
            })()}
          />
        </div>
      </section>

      {/* ============== Bloco: Métricas ============== */}
      <section className="space-y-3">
        <SectionLabel icon={TrendingUp}>Métricas</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          <MetricCard
            icon={MousePointerClick}
            label="Cliques"
            value={affiliate.total_clicks ?? 0}
          />
          <MetricCard
            icon={Users}
            label="Indicações"
            value={affiliate.total_referrals ?? 0}
          />
          <MetricCard
            icon={CheckCircle2}
            label="Conversões"
            value={affiliate.total_conversions ?? 0}
          />
          <MetricCard
            icon={Coins}
            label="Pontos"
            value={affiliate.total_points ?? 0}
          />
          <MetricCard
            icon={TrendingUp}
            label="Total ganho"
            value={formatBRL(affiliate.total_earned)}
          />
          <MetricCard
            icon={Wallet}
            label="Total pago"
            value={formatBRL(affiliate.total_paid)}
          />
          <MetricCard
            icon={Wallet}
            label="Saldo disponível"
            value={formatBRL(affiliate.available_balance)}
            highlight
          />
        </div>
      </section>

      {/* ============== Bloco: Links de divulgação ============== */}
      <section className="space-y-3">
        <AffiliateLinksSection affiliateId={affiliate.id} />
      </section>

      {/* ============== Bloco: Moderação ============== */}
      <section className="space-y-3">
        <SectionLabel icon={CheckCircle2}>Moderação</SectionLabel>
        <div className="rounded-xl border border-border/60 bg-card p-3 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-xs font-medium">
              Motivo (rascunho)
            </Label>
            <Textarea
              id="reason"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Opcional — pré-preenche os diálogos de Suspender / Reprovar."
              className="text-sm resize-none"
            />
          </div>

          {hasMultiplePrograms ? (
            <div className="rounded-md border border-dashed border-border/70 bg-muted/30 p-3 text-xs text-muted-foreground">
              Este afiliado participa de{' '}
              <strong>{memberships?.length}</strong> programas. Use a aba{' '}
              <strong>"Programas"</strong> para aprovar, suspender ou reprovar
              cada adesão individualmente.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {status !== 'active' && (
                <Button
                  size="sm"
                  onClick={onApprove}
                  disabled={statusBusy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {statusBusy ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  {status === 'suspended' ? 'Reativar' : 'Aprovar'}
                </Button>
              )}
              {status === 'active' && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={onSuspend}
                  disabled={statusBusy}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {statusBusy ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <PauseCircle className="h-4 w-4 mr-2" />
                  )}
                  Suspender
                </Button>
              )}
              {status !== 'rejected' && status !== 'active' && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={onReject}
                  disabled={statusBusy}
                >
                  {statusBusy ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  Reprovar
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ============== Bloco: Notas internas ============== */}
      <section className="space-y-3">
        <SectionLabel icon={StickyNote}>Notas internas</SectionLabel>
        <div className="rounded-xl border border-border/60 bg-card p-3 space-y-2">
          <Textarea
            id="notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observações internas sobre o afiliado (visíveis apenas para a equipe)."
            className="text-sm resize-none"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={handleSaveNotes}
            disabled={notesBusy}
            className="w-full sm:w-auto"
          >
            {notesBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar notas
          </Button>
        </div>
      </section>
    </>
  );
}

// ============================================================================
// SUBCOMPONENTE: Aba "Programas" (modelo N:N)
// ============================================================================
function ProgramsTab({
  affiliateName,
  affiliate,
  memberships,
}: {
  affiliateName: string;
  /** v1.4.0: usado para vincular a pessoa a um novo programa */
  affiliate: AffiliateRow;
  memberships: AffiliateMembership[];
}) {
  // v1.4.0: modal de vínculo (reaproveita o modal de cadastro em modo "vincular")
  const [linkOpen, setLinkOpen] = useState(false);
  const {
    approveMembership,
    rejectMembership,
    suspendMembership,
    reactivateMembership,
    isLoading: isMutating,
  } = useAffiliateMembershipActions();

  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    action: 'reject' | 'suspend' | null;
    membershipId: string | null;
    programName: string | null;
  }>({ open: false, action: null, membershipId: null, programName: null });

  const openConfirm = (
    action: 'reject' | 'suspend',
    membershipId: string,
    programName: string | null,
  ) => setConfirmState({ open: true, action, membershipId, programName });

  const handleConfirm = async () => {
    if (!confirmState.membershipId || !confirmState.action) return;
    if (confirmState.action === 'reject') {
      await rejectMembership(confirmState.membershipId);
    } else {
      await suspendMembership(confirmState.membershipId);
    }
    setConfirmState({
      open: false,
      action: null,
      membershipId: null,
      programName: null,
    });
  };

  const confirmCopy =
    confirmState.action === 'reject'
      ? {
          title: 'Rejeitar adesão?',
          description: `Esta ação rejeitará a adesão de ${affiliateName} no programa "${confirmState.programName ?? '—'}".`,
          confirmText: 'Rejeitar',
        }
      : {
          title: 'Suspender adesão?',
          description: `A adesão de ${affiliateName} no programa "${confirmState.programName ?? '—'}" será suspensa.`,
          confirmText: 'Suspender',
        };

  return (
    <div className="space-y-3">
      {/* Ação: vincular a um novo programa (v1.4.0) */}
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
          onClick={() => setLinkOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          Adicionar a programa
        </Button>
      </div>

      {memberships.length === 0 && (
        <p className="text-xs text-muted-foreground py-6 text-center">
          Este afiliado ainda não participa de nenhum programa.
        </p>
      )}

      <AffiliateAdminCreateModal
        open={linkOpen}
        onOpenChange={setLinkOpen}
        affiliate={{
          id: affiliate.id,
          full_name: affiliate.full_name,
          email: affiliate.email,
          phone: affiliate.phone,
          document_number: affiliate.document_number,
          document_type: affiliate.document_type,
          programIds: memberships.map((m) => m.program_id).filter(Boolean) as string[],
        }}
      />

      {memberships.map((m) => {
        const canApprove = m.status === 'pending';
        const canReject = m.status === 'pending';
        const canSuspend = m.status === 'active';
        const canReactivate =
          m.status === 'suspended' || m.status === 'rejected';

        return (
          <div
            key={m.id}
            className="rounded-xl border border-border/60 bg-card p-3 space-y-3"
          >
            {/* Cabeçalho do card */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Layers className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium truncate">
                    {m.program_name ?? '—'}
                  </span>
                </div>
                {m.program_slug && (
                  <p className="text-[11px] text-muted-foreground font-mono truncate mt-0.5">
                    {m.program_slug}
                  </p>
                )}
              </div>
              <AffiliateStatusBadge status={m.status} className="shrink-0" />
            </div>

            {/* KPIs por adesão */}
            <div className="grid grid-cols-3 gap-2">
              <MetricCard
                label="Indicações"
                value={m.total_referrals}
              />
              <MetricCard
                label="Conversões"
                value={m.total_conversions}
              />
              <MetricCard
                label="Saldo"
                value={formatBRL(m.available_balance)}
                highlight
              />
            </div>

            <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
              <span>
                Ganho:{' '}
                <strong className="text-foreground">
                  {formatBRL(m.total_earned)}
                </strong>
              </span>
              <span>
                Pago:{' '}
                <strong className="text-foreground">
                  {formatBRL(m.total_paid)}
                </strong>
              </span>
              <span>
                Aprovação:{' '}
                <strong className="text-foreground">
                  {m.approved_at
                    ? format(new Date(m.approved_at), 'dd/MM/yy', {
                        locale: ptBR,
                      })
                    : '—'}
                </strong>
              </span>
              <span>
                Adesão:{' '}
                <strong className="text-foreground">
                  {format(new Date(m.created_at), 'dd/MM/yy', { locale: ptBR })}
                </strong>
              </span>
            </div>

            {/* Botões de ação por adesão */}
            <div className="flex flex-wrap gap-2 pt-1">
              {canApprove && (
                <Button
                  size="xs"
                  disabled={isMutating}
                  onClick={() => approveMembership(m.id)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Check />
                  Aprovar
                </Button>
              )}
              {canReject && (
                <Button
                  size="xs"
                  variant="destructive"
                  disabled={isMutating}
                  onClick={() => openConfirm('reject', m.id, m.program_name)}
                >
                  <X />
                  Rejeitar
                </Button>
              )}
              {canSuspend && (
                <Button
                  size="xs"
                  variant="secondary"
                  disabled={isMutating}
                  onClick={() => openConfirm('suspend', m.id, m.program_name)}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Pause />
                  Suspender
                </Button>
              )}
              {canReactivate && (
                <Button
                  size="xs"
                  variant="outline"
                  disabled={isMutating}
                  onClick={() => reactivateMembership(m.id)}
                >
                  <RotateCcw />
                  Reativar
                </Button>
              )}
            </div>
          </div>
        );
      })}

      <ConfirmDialog
        open={confirmState.open}
        onOpenChange={(open) => setConfirmState((p) => ({ ...p, open }))}
        variant={confirmState.action === 'reject' ? 'danger' : 'warning'}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmText={confirmCopy.confirmText}
        onConfirm={handleConfirm}
        isLoading={isMutating}
      />
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTE: Links de divulgação do afiliado
// ============================================================================
function AffiliateLinksSection({ affiliateId }: { affiliateId: string }) {
  const { data: links, isLoading } = useReferralAffiliateLinks(affiliateId);
  const count = links?.length ?? 0;

  return (
    <>
      <SectionLabel
        icon={Link2}
        right={
          !isLoading && count > 0 ? (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {count} {count === 1 ? 'link' : 'links'}
            </span>
          ) : undefined
        }
      >
        Links de divulgação
      </SectionLabel>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-20 w-full rounded-xl" />
        </div>
      )}

      {!isLoading && count === 0 && (
        <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 p-3 text-xs text-muted-foreground">
          Nenhum link gerado para este afiliado ainda.
        </div>
      )}

      {!isLoading && count > 0 && (
        <div
          className={cn(
            'space-y-2 min-w-0',
            count > 3 && 'max-h-72 overflow-y-auto pr-1',
          )}
        >
          {links!.map((l: ReferralAffiliateLinkRow) => (
            <LinkCard
              key={l.id}
              link={{
                id: l.id,
                slug: l.slug,
                short_code: l.short_code,
                destination_url: l.destination_url,
                is_active: l.is_active,
                campaign_name: l.campaign_name,
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}
